// frontend/src/components/themed/ThemedButton.js - Professional themed button component
import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ThemedButton = ({ 
  variant = 'primary', 
  size = 'md', 
  disabled = false,
  fullWidth = false,
  type = 'button',
  onClick,
  children,
  className = '',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  ...props 
}) => {
  const { components, states, cn } = useTheme();

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  // Base classes
  const baseClasses = [
    'inline-flex items-center justify-center',
    'font-medium rounded-md',
    'transition-all duration-200',
    states.focus.outline,
    fullWidth ? 'w-full' : '',
    sizeClasses[size],
    disabled || loading ? states.disabled.cursor : '',
  ];

  // Variant classes
  const variantClasses = disabled || loading
    ? components.button.disabled
    : components.button[variant] || components.button.primary;

  // Combine all classes
  const buttonClasses = cn(
    ...baseClasses,
    variantClasses,
    className
  );

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg 
      className="animate-spin h-4 w-4" 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <>
          <LoadingSpinner />
          <span className="ml-2">{children}</span>
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon className="w-4 h-4 mr-2" />}
          {children}
          {Icon && iconPosition === 'right' && <Icon className="w-4 h-4 ml-2" />}
        </>
      )}
    </button>
  );
};

export default ThemedButton;